import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Divider, Tooltip } from '@mui/material';
import classNames from 'classnames';
import { ReactComponent as FloikLogo } from '../../../Common/images/floik-logo-grey.svg';
import {
	CustomControlButtonsType,
	customControlsType,
	customProgressBarType,
} from '../VideoPlayer.type';
import { find, forEach, get, isFunction, isUndefined, replace } from 'lodash';
import {
	FLO_PAGE_VIDEO_DURATION,
	FLO_PAGE_VIDEO_MAXIMIZE,
	FLO_PAGE_VIDEO_MINIMIZE,
	FLO_PAGE_VIDEO_PAUSE,
	FLO_PAGE_VIDEO_PLAY,
	FLO_PAGE_VIDEO_PLAYBACK_SPEED,
	FLO_PAGE_VIDEO_SUBTITLES,
	FLO_PAGE_VIDEO_VOLUME,
} from '../../../Common/Common.constants';
import { useDispatch, useSelector } from 'react-redux';
import uniqId from 'uniqid';
import {
	currentProfileSelector,
	enablePoweredBYSelector,
} from '../../../Common/Common.selector';
import { IsIpad } from '../../../utils/IsPhone';
import sanitizeHtml from 'sanitize-html';
import { APP_ENDPOINT, APP_WEBSITE_URL } from '../../../Common/Common.env';
import {
	IconMaximize,
	IconMessageCirclePlus,
	IconMinimize,
	IconPlayerPauseFilled,
	IconPlayerPlay,
	IconPlayerPlayFilled,
	IconVolume,
	IconVolume3,
} from '@tabler/icons-react';
import { ReactComponent as IconBadgeCc } from './subtitle.svg';
import MenuList, { Option } from '../../MenuList/MenuList';
import { PLAYBACK_SPEED_OPTIONS } from '../../../Common/Common.params';
import {
	setAllowComment,
	setAnnotationToolAction,
	setTrimFirstPlay,
	switchFullScreen,
	trackClickAction,
} from '../../../Pages/Flo/Flo.reducers';
import ShareFloButton from '../../ShareFloButton/ShareFloButton';
import InteractiveModeControls from '../InteractiveModeControls/InteractiveModeControls';
import DialogComponent from '../../VideoDialog/VideoDialog';
import { setAutoPlay } from '../../../Pages/Flo/Flo.actions';
import { createPortal } from 'react-dom';
import { RootState } from '../../../store';
import styles from './CustomInVideoControls.module.css';
import { isMicroApp } from '../../../Common/Common.utils';

const isIpad = IsIpad();

const CustomInVideoControls = ({
	autoPlay,
	hasAudioTrack,
	isAiVoice,
	disabledControls,
	sliderIndex,
	setCurrentElementIndex,
	hidePlayButton,
	children,
	style,
	hideHeader,
	className,
	classes,
	onPlay,
	onPause,
	nodeRef,
	presenterNodeRef,
	usePresenterNodeForVolume,
	progressRef,
	onVolumeChange,
	durationRef,
	customControlsChildren,
	onClickProgressBar,
	floName,
	createdBy,
	createdOn,
	volume,
	width,
	height,
	offsetHeight,
	offsetWidth,
	addAnnotationHandler,
	videoReady,
	onPlaybackRatesChange,
	setIsFullScreen,
	isFullScreen,
	textTrack,
	hasInitialhotspot,
	currentHotspot,
	isTabView,
	showEndCta,
	goToStep,
	activityPosition,
	resetPrevHotspot,
	hidePlayer,
	resetLastSeekedTime,
	onArrowClickCb,
	currentElementType,
	onRestart,
	hidePlayIcon,
	showSeekbar,
	floId,
	floIndex,
	videoContainerRef,
}: customControlsType) => {
	const CustomControlButtonsProps = {
		autoPlay,
		onPlay,
		onPause,
		nodeRef,
		presenterNodeRef,
		usePresenterNodeForVolume,
		onVolumeChange,
		durationRef,
		customControlsChildren,
		onPlaybackRatesChange,
		setIsFullScreen,
		isFullScreen,
		textTrack,
		currentHotspot,
		isTabView,
		showEndCta,
		goToStep,
		hasAudioTrack,
		isAiVoice,
		width,
		resetPrevHotspot,
		onArrowClickCb,
		currentElementType,
		sliderIndex,
		setCurrentElementIndex,
		onRestart,
		showSeekbar,
		volume,
		videoContainerRef,
	};
	const customProgressBarProps = {
		progressRef,
		onClickProgressBar,
		floIndex,
	};
	const dispatch = useDispatch();
	const dialogId = useSelector((state) => get(state, `videoDialog.${floIndex}.id`));

	const customControlsRef = useRef<HTMLDivElement>(null);
	const volumeContainerRef = useRef<HTMLDivElement>(null);

	const allowComment = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.allowComment`, false)
	);
	const [keepInView, setKeepInView] = useState(false);
	const currentUser = useSelector(currentProfileSelector);
	const showAuthor = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.floDetails["show-author"]`, true)
	);
	const hasNotPlayed = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.postProcessConfigs.trim.hasNotPlayed`, true)
	);
	const showAppComment = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.appCommentPosition.show`)
	);
	const [longTimout, setLongTimeout] = useState<NodeJS.Timeout | undefined>();
	const [touchStared, setTouchStarted] = useState(false);
	const [mousePointer, setMousePointer] = useState({ x: 0, y: 0 });
	const interactiveMode = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.floDetails["interactive-mode"]`)
	);
	const isFirstPlayRef = useRef<boolean>(false);
	const playing = !nodeRef?.current?.paused() && !nodeRef?.current?.seeking();
	const showFloTitle = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.floDetails["show-flo-title"]`, true)
	);

	useEffect(() => {
		if (hasNotPlayed && interactiveMode) {
			isFirstPlayRef.current = true;
		} else if (isFirstPlayRef.current && !(hasNotPlayed && interactiveMode)) {
			const timeout = setTimeout(() => {
				isFirstPlayRef.current = false;
				clearTimeout(timeout);
			}, 1300);
		}
	}, [hasNotPlayed, interactiveMode, isFirstPlayRef]);

	const onLongTouch = useCallback(() => {
		if (touchStared && isFunction(addAnnotationHandler)) {
			if (isFunction(onPause)) onPause();
			// @ts-ignore
			addAnnotationHandler({
				uid: uniqId(),
				// @ts-ignore
				time: nodeRef?.current?.currentTime() || 0,
				state: [
					{
						key: uniqId(),
						tool: 'rect',
						// @ts-ignore
						time: nodeRef?.current?.currentTime() || 0,
						windowSize: {
							height,
							width,
							offsetHeight: offsetHeight || 0,
							offsetWidth: offsetWidth || 0,
						},
						x: mousePointer.x || 0,
						y: mousePointer.y || 0,
						width: 10,
						height: 10,
						points: [],
						createdBy: {
							firstName: get(currentUser, 'firstName', ''),
							name: get(currentUser, 'name', ''),
							// @ts-ignore
							userProfile: currentUser,
						},
					},
				],
			});
		}
	}, [onPause, touchStared, mousePointer, currentUser]);

	const onPoweredByClick = useCallback(() => {
		const currentTime = nodeRef?.current?.currentTime() || 0;
		dispatch(
			trackClickAction({
				body: {
					videoTime: currentTime,
					buttonType: 'poweredByFloik',
				},
				floId,
				floIndex,
			})
		);
	}, [nodeRef]);

	const onTouchEnd = useCallback(() => {
		setTouchStarted(false);
		if (longTimout) {
			clearTimeout(longTimout);
		}
	}, [onLongTouch]);

	const onTouchStart = useCallback(
		(event: React.TouchEvent<HTMLDivElement>) => {
			try {
				event.preventDefault();
			} catch (e) {
				console.error(e);
			}
			try {
				if (!event.target || !videoReady) return;
				setTouchStarted(true);

				// @ts-ignore
				const { x, y } = event.target.getBoundingClientRect();
				setMousePointer({
					x: get(event, 'targetTouches[0].clientX') - x,
					y: get(event, 'targetTouches[0].clientY') - y,
				});
				if (longTimout) {
					clearTimeout(longTimout);
				}
				const timeout = setTimeout(onLongTouch, 500);
				setLongTimeout(timeout);
			} catch (error) {}
		},
		[setLongTimeout, videoReady, onLongTouch, setMousePointer]
	);

	const hotspotsTimes = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.hotspotTimes`)
	);
	const startCta = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.editorsElements.info_ctastart[0]`)
	);
	useState(false);
	const [showingHotSpot, setShowingHotSpot] = useState(false);

	useEffect(() => {
		if (!nodeRef?.current) return;
		if (playing) {
			setShowingHotSpot(false);
			return;
		}

		const currentTime = nodeRef.current?.currentTime() || 0;
		const hotspotTime = find(
			hotspotsTimes,
			(item: number) => currentTime >= item - 0.1 && currentTime < item + 0.35
		);
		const hasHotspot = !isUndefined(hotspotTime);
		setShowingHotSpot(playing ? false : hasHotspot);
	}, [
		setShowingHotSpot,
		startCta,
		hotspotsTimes,
		hasNotPlayed,
		playing,
		nodeRef?.current,
		currentHotspot,
	]);

	const playPause = useCallback(() => {
		if (nodeRef?.current) {
			// @ts-ignore
			const isPaused = !!nodeRef?.current?.paused();
			if (isPaused) {
				if (isFunction(onPlay)) onPlay();
			} else {
				if (isFunction(onPause)) onPause({ manual: true });
			}
		}
	}, [nodeRef, onPlay, onPause]);

	const onTogglePlay = useCallback(() => {
		if (showingHotSpot) {
			dispatch(setTrimFirstPlay({ data: false, floId, floIndex }));
		} else {
			playPause();
		}
		dispatch(setAutoPlay({ data: false, floId, floIndex }));
		dispatch(setTrimFirstPlay({ data: false, floId, floIndex }));
	}, [showingHotSpot, nodeRef, playPause]);

	useEffect(() => {
		if (!nodeRef?.current) return;
		// @ts-ignore
		const onEnded = () => {
			if (!nodeRef?.current) return;
			const newRate = nodeRef.current?.playbackRate();
			dispatch(setTrimFirstPlay({ data: true, floId, floIndex }));
			nodeRef.current?.currentTime(0);
		};
		const onPlay = () => {
			dispatch(setTrimFirstPlay({ data: false, floId, floIndex }));
		};
		nodeRef.current.on('play', onPlay);
		nodeRef.current.on('ended', onEnded);
	}, [nodeRef?.current]);

	return (
		<div
			className={classNames(styles.customControls, className, {
				[styles.hideHeader]: hideHeader,
				[styles.interactiveMode]: interactiveMode,
			})}
			ref={customControlsRef}
			style={style}
		>
			{children}

			{!showAppComment && videoReady && !hidePlayer && (
				<div
					className={classNames(styles.overlay, classes?.overlay, {
						[styles.showButton]:
							(hasNotPlayed && !interactiveMode) || (!playing && !showingHotSpot),
						[styles.showClickHereButton]: hasNotPlayed && interactiveMode && !dialogId,
						[styles.firstPlay]: hasNotPlayed && interactiveMode,
						[styles.bringToFront]: hasNotPlayed,
						[styles.fadeOutAnimation]:
							isFirstPlayRef.current && !hasNotPlayed && interactiveMode,
					})}
					id="video-overlay"
					onClick={onTogglePlay}
					onTouchEnd={onTouchEnd}
					onTouchStart={onTouchStart}
					onTouchStartCapture={onTouchStart}
				>
					{playing ||
					hidePlayIcon ||
					(interactiveMode && !hasNotPlayed) ||
					(showingHotSpot && !hasNotPlayed) ||
					(allowComment && !hasNotPlayed) ||
					dialogId
						? ''
						: !autoPlay && (
								<div className={classNames(styles.showPlayButton)}>
									{interactiveMode && <div className={styles.interactivePlaying}></div>}
									<IconPlayerPlay size={37} className={styles.playIcon} />
								</div>
						  )}

					{/* {autoPlay && <div
						ref={volumeContainerRef}
					>
					</div>} */}
				</div>
			)}
			{!interactiveMode && hideHeader && showFloTitle && (
				<div className={styles.header}>
					<div
						className={styles.floTitle}
						dangerouslySetInnerHTML={{
							__html: sanitizeHtml(floName || ''),
						}}
					></div>
					<div className={styles.floDetails}>
						{showAuthor ? `${createdBy} -` : ''} {createdOn}
					</div>
				</div>
			)}

			<div
				className={classNames(
					classNames(styles.customControlsContainer, {
						[styles.keepInView]: keepInView,
						[styles.hide]: interactiveMode || hasNotPlayed,
						[styles.forceHidden]: autoPlay,
					})
				)}
			>
				{!interactiveMode && <CustomProgressBar {...customProgressBarProps} />}
				<CustomControlButtons
					volumeContainerRef={volumeContainerRef}
					onPoweredByClick={onPoweredByClick}
					keepInView={keepInView}
					setKeepInView={setKeepInView}
					interactiveMode={interactiveMode}
					onTogglePlay={playPause}
					container={customControlsRef.current}
					floId={floId}
					floIndex={floIndex}
					{...CustomControlButtonsProps}
				/>
			</div>
			{interactiveMode && !hasNotPlayed && (
				<InteractiveModeControls
					{...CustomControlButtonsProps}
					volumeContainerRef={volumeContainerRef}
					onPoweredByClick={onPoweredByClick}
					disabledControls={disabledControls}
					playing={playing}
					onTogglePlay={playPause}
					customProgressBarProps={customProgressBarProps}
					parentRef={customControlsRef}
					resetLastSeekedTime={resetLastSeekedTime}
					activityPosition={activityPosition}
					floId={floId}
					floIndex={floIndex}
				/>
			)}
			{isTabView && (
				<DialogComponent
					floId={floId}
					floIndex={floIndex}
					isTabView={isTabView}
					style={{
						width: Math.ceil(width),
						height: Math.ceil(height),
					}}
				/>
			)}
		</div>
	);
};

export const CustomProgressBar = ({
	progressRef,
	onClickProgressBar,
	interactiveMode,
	classes,
	floId,
	floIndex,
}: customProgressBarType) => {
	const trimConfigs = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.postProcessConfigs.trim`)
	);
	const trimEnabled = get(trimConfigs, 'enable', false);

	return (
		<div
			id={`floikCustomProgressBar_${floIndex}`}
			className={classNames(
				'custom-vjs-progress-control',
				'vjs-progress-control',
				styles.progressContainer,
				classes?.progressContainer || '',
				{ [styles.hide]: trimEnabled, [styles.interactiveMode]: interactiveMode }
			)}
			onTouchEnd={onClickProgressBar}
			onClick={onClickProgressBar}
		>
			<div
				// @ts-ignore
				ref={progressRef}
				className={styles.progress}
			/>
		</div>
	);
};
const SpeedMenu = ({ selectedKey }: { selectedKey?: number }) => {
	return (
		<div className={styles.speed}>
			<span className={styles.speedText}>{selectedKey || 1}x</span>
		</div>
	);
};

function isElementInFullscreen() {
	return (
		document.fullscreenElement ||
		// @ts-ignore
		document.mozFullScreenElement ||
		// @ts-ignore
		document.webkitFullscreenElement ||
		// @ts-ignore
		document.msFullscreenElement
	);
}

let parentDomain = '';
try {
	const domain = window.location !== window.parent.location ? document.referrer : '';
	parentDomain = replace(domain, /\/$/, '');
} catch (e) {
	console.error(e);
}
const canComment = !parentDomain ? true : parentDomain !== APP_ENDPOINT;

const CustomControlButtons = ({
	onPlay,
	onPause,
	nodeRef,
	presenterNodeRef,
	usePresenterNodeForVolume,
	onVolumeChange,
	durationRef,
	volume,
	setKeepInView,
	keepInView,
	onPlaybackRatesChange,
	isFullScreen,
	textTrack,
	setIsFullScreen: setIsFullScreenCB,
	isTabView,
	width,
	hasAudioTrack,
	isAiVoice,
	onPoweredByClick,
	container,
	volumeContainerRef,
	floId,
	floIndex,
	videoContainerRef,
}: CustomControlButtonsType) => {
	const volumeRef = usePresenterNodeForVolume ? presenterNodeRef : nodeRef;

	const dispatch = useDispatch();
	const showPoweredBy = useSelector(enablePoweredBYSelector);
	// @ts-ignore
	const playing = !nodeRef?.current?.paused();
	// const [isFullScreen, setIsFullScreen] = useState(false);
	const [isMuted, setMuted] = useState(!get(volumeRef, 'current.volume', 1));
	const [rate, setRate] = useState(1);
	const [hideSubtitle, setHideSubtitle] = useState(false);
	const enableAnnotation = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.floDetails.allow_public_comments`)
	);
	const [tooltipParent, setTooltipParent] = useState<HTMLElement | null>(null);

	const isNoVoice = useSelector((state: RootState): boolean => {
		const elementsConfig = get(
			state,
			`floPage.flos.${floIndex}.editorsElementsConfig`,
			[]
		);
		const selectedElementsConfig = find(
			elementsConfig,
			(e) => get(e, 'editorElementName') === 'voice-over'
		);
		const voiceOverProperties = get(selectedElementsConfig, 'properties');
		return get(voiceOverProperties, 'voiceOverMode') === 'NO_VOICE';
	});
	useEffect(() => {
		setMuted(volume === 0);
	}, [setMuted, volume]);

	const allowComment = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.allowComment`, false)
	);

	useEffect(() => {
		if (!videoContainerRef?.current) return;
		videoContainerRef.current.onfullscreenchange = (e) => {
			const isFullSc = isElementInFullscreen() as boolean;
			dispatch(
				switchFullScreen({
					data: isFullSc,
					floId,
					floIndex,
				})
			);
			if (isFunction(setIsFullScreenCB)) setIsFullScreenCB(isFullSc);
		};
	}, [setIsFullScreenCB, videoContainerRef?.current]);

	useEffect(() => {
		if (!nodeRef?.current) return;
		// @ts-ignore
		const newRate = nodeRef.current?.playbackRate();
		setRate(newRate);
		const onRateChange = () => {
			if (!nodeRef?.current) return;
			const newRate = nodeRef.current?.playbackRate();
			setRate(newRate);
		};
		nodeRef.current.on('ratechange', onRateChange);
	}, [setRate, nodeRef?.current]);

	const onTogglePlay = useCallback(() => {
		if (nodeRef?.current) {
			// @ts-ignore
			const isPaused = !!nodeRef?.current?.paused();
			if (isPaused) {
				if (isFunction(onPlay)) onPlay();
			} else {
				if (isFunction(onPause)) onPause({ manual: true });
			}
		}
	}, [nodeRef, onPlay, onPause]);

	const onClose = useCallback(() => {
		if (isFunction(setKeepInView)) {
			setKeepInView(false);
		}
	}, [setKeepInView]);

	const onAnchorClick = useCallback(() => {
		if (isFunction(setKeepInView)) {
			setKeepInView(true);
		}
	}, [setKeepInView]);

	const onPlaybackChangeCallback = useCallback(
		(
			event: React.MouseEvent<HTMLElement>,
			val: Option,
			onClose: (val: unknown) => void
		) => {
			if (isFunction(onPlaybackRatesChange)) onPlaybackRatesChange(get(val, 'value', 1));
			if (isFunction(onClose)) onClose();
		},
		[onPlaybackRatesChange]
	);

	const onToggleComment = useCallback(() => {
		dispatch(setAnnotationToolAction({ data: 'rect', floId, floIndex }));
		dispatch(setAllowComment({ data: allowComment, floId, floIndex }));
	}, [allowComment]);

	const toggleSubtitles = useCallback(() => {
		if (!nodeRef?.current?.remoteTextTracks || !textTrack) return;
		const textTracks = nodeRef.current?.remoteTextTracks();
		forEach(textTracks, (track) => {
			if (hideSubtitle) {
				track.mode = 'showing';
			} else {
				track.mode = 'hidden';
			}
		});
		setHideSubtitle((i) => !i);
	}, [nodeRef?.current, textTrack, hideSubtitle, setHideSubtitle]);

	const toggleFullScreen = useCallback(() => {
		const videoContainer = videoContainerRef?.current;
		if (!isFullScreen) {
			// @ts-ignore
			if (videoContainer.requestFullscreen) {
				// @ts-ignore
				videoContainer.requestFullscreen();
				// @ts-ignore
			} else if (videoContainer.mozRequestFullScreen) {
				// @ts-ignore
				videoContainer.mozRequestFullScreen();
				// @ts-ignore
			} else if (videoContainer.webkitRequestFullscreen) {
				// @ts-ignore
				videoContainer.webkitRequestFullscreen();
				// @ts-ignore
			} else if (videoContainer.msRequestFullscreen) {
				// @ts-ignore
				videoContainer.msRequestFullscreen();
			}
		}
		if (isFullScreen) {
			// @ts-ignore
			if (document.exitFullscreen) {
				// @ts-ignore
				document.exitFullscreen();
				// @ts-ignore
			} else if (document.mozCancelFullScreen) {
				// @ts-ignore
				document.mozCancelFullScreen();
				// @ts-ignore
			} else if (document.webkitExitFullscreen) {
				// @ts-ignore
				document.webkitExitFullscreen();
				// @ts-ignore
			} else if (document.msExitFullscreen) {
				// @ts-ignore
				document.msExitFullscreen();
			}
		}
	}, [isFullScreen, videoContainerRef?.current]);

	useEffect(() => {
		// @ts-ignore
		setTooltipParent(
			document.querySelectorAll('[class*=fullScreenContainer]')?.[floIndex]
		);
	}, [isFullScreen, setTooltipParent, floIndex]);

	const addAnnotationClick = useCallback(() => {
		if (!enableAnnotation) return;
		if (nodeRef?.current) {
			nodeRef.current?.pause();
		}
		dispatch(setAnnotationToolAction({ data: 'rect', floId, floIndex }));
	}, [enableAnnotation, nodeRef]);

	const hasTextTrack = !!textTrack;
	const showMuted = !isAiVoice && !hasAudioTrack;
	const belowMobileRange = width && width < 300;
	const toggleMute = useCallback(() => {
		if (isFunction(onVolumeChange)) {
			onVolumeChange({
				isMuted: !isMuted,
				volume: !isMuted ? 0 : 1,
			});
			setMuted(!isMuted);
		}
	}, [onVolumeChange, setMuted, isMuted]);

	return (
		<div className={classNames(styles.buttonControllerContainer)}>
			{volumeContainerRef?.current &&
				createPortal(
					<div className={styles.floatingMuteIcon} onClick={toggleMute}>
						{isNoVoice || showMuted || isMuted || volume === 0 ? (
							<IconVolume3 size={20} />
						) : (
							<IconVolume size={20} />
						)}
					</div>,
					volumeContainerRef.current
				)}
			<div className={styles.leftCustomControls}>
				<Tooltip
					classes={{
						popper: styles.tooltip,
					}}
					arrow
					title={playing ? FLO_PAGE_VIDEO_PAUSE : FLO_PAGE_VIDEO_PLAY}
					placeholder="bottom-end"
					placement="top"
					PopperProps={{
						container: tooltipParent,
					}}
				>
					<div
						className={classNames(styles['play-status'], styles.controlButton)}
						onClick={onTogglePlay}
					>
						{playing ? (
							<IconPlayerPauseFilled size={20} />
						) : (
							<IconPlayerPlayFilled size={20} />
						)}
					</div>
				</Tooltip>
				{!isIpad && (
					<Tooltip
						classes={{
							popper: styles.tooltip,
						}}
						arrow
						title={FLO_PAGE_VIDEO_VOLUME}
						placeholder="bottom-end"
						placement="top"
						PopperProps={{
							container: tooltipParent,
						}}
					>
						<div
							className={classNames(styles.sound, styles.controlButton)}
							onClick={toggleMute}
						>
							{isNoVoice || showMuted || isMuted || volume === 0 ? (
								<IconVolume3 size={20} />
							) : (
								<IconVolume size={20} />
							)}
						</div>
					</Tooltip>
				)}
				<Tooltip
					classes={{
						popper: styles.tooltip,
					}}
					arrow
					title={FLO_PAGE_VIDEO_DURATION}
					placeholder="bottom-end"
					placement="top"
					PopperProps={{
						container: tooltipParent,
					}}
				>
					<div
						// @ts-ignore
						ref={durationRef}
						className={classNames(styles.duration, styles.controlButton)}
					/>
				</Tooltip>
			</div>
			<div className={styles.rightCustomControls}>
				{canComment && enableAnnotation && !belowMobileRange && false && (
					<div
						className={classNames(styles.leaveComment, {
							[styles.subTitleInactive]: !allowComment,
						})}
						onClick={onToggleComment}
					>
						<IconMessageCirclePlus />
						{!isTabView && (
							<span className={styles.leaveCommentText}>Leave a comment</span>
						)}
					</div>
				)}
				{!belowMobileRange && (
					<Tooltip
						classes={{
							popper: styles.tooltip,
						}}
						arrow
						title={keepInView ? '' : FLO_PAGE_VIDEO_PLAYBACK_SPEED}
						placeholder="bottom-end"
						placement="top"
						PopperProps={{
							container: tooltipParent,
						}}
					>
						<div>
							<MenuList
								onAnchorClick={onAnchorClick}
								onClose={onClose}
								anchorIcon={SpeedMenu}
								selectedKey={rate}
								onMenuClick={onPlaybackChangeCallback}
								options={PLAYBACK_SPEED_OPTIONS}
								anchorPlacement={'top'}
								container={isFullScreen ? container : undefined}
							/>
						</div>
					</Tooltip>
				)}
				{!belowMobileRange && hasTextTrack && (
					<Tooltip
						classes={{
							popper: styles.tooltip,
						}}
						arrow
						title={FLO_PAGE_VIDEO_SUBTITLES}
						placeholder="bottom-end"
						placement="top"
						PopperProps={{
							container: tooltipParent,
						}}
					>
						<div
							className={classNames(styles.subTitle, {
								[styles.subTitleInactive]: hideSubtitle,
							})}
							onClick={toggleSubtitles}
						>
							<IconBadgeCc className={styles.iconCC} />
						</div>
					</Tooltip>
				)}
				{!isMicroApp && (
					<ShareFloButton
						floId={floId}
						floIndex={floIndex}
						classes={{
							shareIcon: styles.shareIcon,
							shareButton: styles.sharButton,
						}}
						tooltipProps={{ title: '' }}
						iconProps={{ size: 20 }}
						onShareClick={onPause}
					/>
				)}
				<Tooltip
					classes={{
						popper: styles.tooltip,
					}}
					arrow
					title={isFullScreen ? FLO_PAGE_VIDEO_MINIMIZE : FLO_PAGE_VIDEO_MAXIMIZE}
					placeholder="bottom-end"
					placement="top"
					PopperProps={{
						container: tooltipParent,
					}}
				>
					<div className={styles.maximize} onClick={toggleFullScreen}>
						{isFullScreen ? <IconMinimize size={20} /> : <IconMaximize size={20} />}
					</div>
				</Tooltip>
				{showPoweredBy && !isTabView && <Divider className={styles.divider} />}
				{showPoweredBy && (
					<div
						className={classNames('videoPoweredby', {
							videoPoweredby450: width && width < 450,
						})}
					>
						<a
							href={APP_WEBSITE_URL}
							target="_blank"
							className={classNames(styles.poweredByLogoLink, 'link')}
							onClick={onPoweredByClick}
						>
							Powered by
							<FloikLogo className={styles.floikSVGIcon} />
						</a>
					</div>
				)}
			</div>
		</div>
	);
};

export default CustomInVideoControls;
